/* Book Appointment — multi-step booking flow backed by the Calendly proxy.
   Steps: 0 landing, 1 date, 2 time (same panel as 1), 3 details form, 4 confirmation. */

(function () {
  const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const CACHE_TTL_MS = 5 * 60 * 1000;

  class BookAppointment extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const configEl = this.querySelector('[data-ba-config]');
      this.config = configEl ? JSON.parse(configEl.textContent) : {};
      this.strings = this.config.strings || {};
      this.timezone = this.config.timezone || 'America/New_York';

      this.panels = {
        0: this.querySelector('[data-step="0"]'),
        1: this.querySelector('[data-step="1"]'),
        3: this.querySelector('[data-step="3"]'),
        4: this.querySelector('[data-step="4"]'),
      };
      this.live = this.querySelector('[data-ba-live]');
      this.grid = this.querySelector('[data-ba-grid]');
      this.monthLabel = this.querySelector('[data-ba-month-label]');
      this.prevButton = this.querySelector('[data-ba-prev-month]');
      this.nextButton = this.querySelector('[data-ba-next-month]');
      this.hint = this.querySelector('[data-ba-hint]');
      this.slotsEl = this.querySelector('[data-ba-slots]');
      this.continueButton = this.querySelector('[data-ba-continue]');
      this.errorEl = this.querySelector('[data-ba-error]');
      this.errorMessage = this.querySelector('[data-ba-error-message]');
      this.form = this.querySelector('[data-ba-form]');
      this.submitButton = this.querySelector('[data-ba-submit]');
      this.formError = this.querySelector('[data-ba-form-error]');
      this.formErrorMessage = this.querySelector('[data-ba-form-error-message]');
      this.slotLabelEl = this.querySelector('[data-ba-slot-label]');
      this.confirmSlotEl = this.querySelector('[data-ba-confirm-slot]');

      this.state = {
        step: 0,
        month: this.currentMonthKey(),
        selectedDate: null,
        selectedSlot: null,
        availability: new Map(),
        fetchController: null,
      };

      this.todayKey = this.dateKeyInTz(new Date());

      this.bindEvents();
      this.renderCalendar();
      history.replaceState({ baStep: 0 }, '');
    }

    /* ---------- Timezone helpers ---------- */

    // "YYYY-MM-DD" for an instant, in the showroom timezone.
    dateKeyInTz(date) {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: this.timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
      return parts;
    }

    currentMonthKey() {
      return this.dateKeyInTz(new Date()).slice(0, 7);
    }

    monthKeyOffset(monthKey, offset) {
      const [y, m] = monthKey.split('-').map(Number);
      const total = y * 12 + (m - 1) + offset;
      const ny = Math.floor(total / 12);
      const nm = (total % 12) + 1;
      return ny + '-' + String(nm).padStart(2, '0');
    }

    monthDiff(fromKey, toKey) {
      const [fy, fm] = fromKey.split('-').map(Number);
      const [ty, tm] = toKey.split('-').map(Number);
      return ty * 12 + tm - (fy * 12 + fm);
    }

    formatTime(date, withMinutesAlways) {
      const raw = new Intl.DateTimeFormat('en-US', {
        timeZone: this.timezone,
        hour: 'numeric',
        minute: '2-digit',
      }).format(date);
      let label = raw.replace(/\s/g, '');
      if (!withMinutesAlways) label = label.replace(':00', '');
      return label.toUpperCase();
    }

    slotButtonLabel(startIso, durationMinutes) {
      const start = new Date(startIso);
      const startLabel = this.formatTime(start);
      if (this.config.slotLabelFormat === 'start') return startLabel;
      const end = new Date(start.getTime() + durationMinutes * 60000);
      return startLabel + ' - ' + this.formatTime(end);
    }

    selectedSlotHeadline() {
      const { selectedSlot } = this.state;
      if (!selectedSlot) return '';
      const start = new Date(selectedSlot.start);
      const dayLabel = new Intl.DateTimeFormat('en-US', {
        timeZone: this.timezone,
        month: 'long',
        day: 'numeric',
      }).format(start);
      const abbr = this.config.timezoneAbbr ? ' ' + this.config.timezoneAbbr : '';
      return dayLabel + ' at ' + this.formatTime(start) + abbr;
    }

    /* ---------- Events ---------- */

    bindEvents() {
      const startButton = this.querySelector('[data-ba-start]');
      if (startButton) startButton.addEventListener('click', () => this.goToStep(1));

      this.prevButton.addEventListener('click', () => this.changeMonth(-1));
      this.nextButton.addEventListener('click', () => this.changeMonth(1));
      this.grid.addEventListener('keydown', (event) => this.onGridKeydown(event));

      this.continueButton.addEventListener('click', () => {
        if (this.state.selectedSlot) this.goToStep(3);
      });

      const changeDate = this.querySelector('[data-ba-change-date]');
      if (changeDate) changeDate.addEventListener('click', () => this.goToStep(1));

      const retry = this.querySelector('[data-ba-retry]');
      if (retry) {
        retry.addEventListener('click', () => {
          this.state.availability.delete(this.state.month);
          this.hideError();
          this.loadMonth(this.state.month);
        });
      }

      this.slotsEl.addEventListener('scroll', () => this.updateSlotsScrollState());

      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.submit();
        });
        this.bindGuestStepper();
      }

      window.addEventListener('popstate', (event) => {
        const step = event.state && typeof event.state.baStep === 'number' ? event.state.baStep : 0;
        this.showStep(this.validStep(step), { focus: false });
      });

      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.syncSlotsMaxHeight(), 150);
      });
    }

    bindGuestStepper() {
      const minus = this.querySelector('[data-ba-guests-minus]');
      const plus = this.querySelector('[data-ba-guests-plus]');
      const value = this.querySelector('[data-ba-guests-value]');
      const input = this.querySelector('[data-ba-guests-input]');
      if (!minus || !plus || !value || !input) return;
      const max = Number(this.config.maxGuests) || 3;
      const update = (delta) => {
        const next = Math.min(max, Math.max(0, Number(input.value || 0) + delta));
        input.value = String(next);
        value.textContent = String(next);
        minus.disabled = next === 0;
        plus.disabled = next === max;
      };
      minus.addEventListener('click', () => update(-1));
      plus.addEventListener('click', () => update(1));
      update(0);
    }

    /* ---------- Step navigation ---------- */

    validStep(step) {
      if (step >= 4) return this.state.step === 4 ? 4 : 0;
      if (step >= 3 && !this.state.selectedSlot) return this.state.month ? 1 : 0;
      return step;
    }

    goToStep(step, options) {
      this.showStep(step, options);
      if (step === 4) {
        history.replaceState({ baStep: step }, '', '#booked');
      } else {
        history.pushState({ baStep: step }, '', '#step-' + step);
      }
    }

    showStep(step, options) {
      const focus = !options || options.focus !== false;
      this.state.step = step;
      const panelKey = step === 2 ? 1 : step;
      Object.entries(this.panels).forEach(([key, panel]) => {
        if (panel) panel.hidden = Number(key) !== panelKey;
      });

      if (step === 1 || step === 2) {
        this.loadMonth(this.state.month);
        this.renderSide();
      }
      if (step === 3) {
        this.slotLabelEl.textContent = this.selectedSlotHeadline();
      }

      const panel = this.panels[panelKey];
      if (panel && focus) {
        const heading = panel.querySelector('[tabindex="-1"]');
        if (heading) heading.focus({ preventScroll: true });
        this.scrollIntoView({ block: 'start' });
      }
      this.announce(panel);
    }

    announce(panel) {
      if (!this.live || !panel) return;
      const heading = panel.querySelector('h1, h2');
      this.live.textContent = heading ? heading.textContent.trim() : '';
    }

    /* ---------- Calendar ---------- */

    changeMonth(offset) {
      const next = this.monthKeyOffset(this.state.month, offset);
      const diffFromNow = this.monthDiff(this.currentMonthKey(), next);
      const horizon = (Number(this.config.windowMonths) || 3) - 1;
      if (diffFromNow < 0 || diffFromNow > horizon) return;
      this.state.month = next;
      this.renderCalendar();
      this.loadMonth(next);
      this.renderSide();
    }

    renderCalendar() {
      const monthKey = this.state.month;
      const [year, month] = monthKey.split('-').map(Number);

      const captionDate = new Date(Date.UTC(year, month - 1, 15));
      this.monthLabel.textContent = new Intl.DateTimeFormat('en-US', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(captionDate);

      const diffFromNow = this.monthDiff(this.currentMonthKey(), monthKey);
      const horizon = (Number(this.config.windowMonths) || 3) - 1;
      this.prevButton.disabled = diffFromNow <= 0;
      this.nextButton.disabled = diffFromNow >= horizon;

      const entry = this.state.availability.get(monthKey);
      const slotsByDate = entry ? entry.slotsByDate : null;
      this.grid.setAttribute('aria-busy', slotsByDate ? 'false' : 'true');

      const frag = document.createDocumentFragment();

      const headerRow = document.createElement('div');
      headerRow.className = 'ba-calendar__row';
      headerRow.setAttribute('role', 'row');
      WEEKDAYS.forEach((day) => {
        const cell = document.createElement('span');
        cell.className = 'ba-calendar__cell ba-calendar__cell--weekday';
        cell.setAttribute('role', 'columnheader');
        cell.textContent = day;
        headerRow.appendChild(cell);
      });
      frag.appendChild(headerRow);

      const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
      const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();

      let row = this.newRow(frag);
      for (let i = 0; i < firstWeekday; i++) row.appendChild(this.emptyCell());

      let firstEnabled = null;
      for (let day = 1; day <= daysInMonth; day++) {
        if (row.children.length === 7) row = this.newRow(frag);
        const dateKey = monthKey + '-' + String(day).padStart(2, '0');
        const cell = document.createElement('button');
        cell.type = 'button';
        cell.className = 'ba-calendar__cell';
        cell.setAttribute('role', 'gridcell');
        cell.dataset.date = dateKey;
        cell.textContent = String(day);
        const hasSlots = !!(slotsByDate && slotsByDate[dateKey] && slotsByDate[dateKey].length);
        const isPast = dateKey < this.todayKey;
        cell.disabled = isPast || !hasSlots;
        cell.setAttribute('aria-selected', dateKey === this.state.selectedDate ? 'true' : 'false');
        cell.setAttribute(
          'aria-label',
          new Intl.DateTimeFormat('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            timeZone: 'UTC',
          }).format(new Date(Date.UTC(year, month - 1, day)))
        );
        cell.tabIndex = -1;
        if (!cell.disabled && (dateKey === this.state.selectedDate || !firstEnabled)) {
          firstEnabled = cell;
        }
        cell.addEventListener('click', () => this.selectDate(dateKey));
        row.appendChild(cell);
      }
      while (row.children.length < 7) row.appendChild(this.emptyCell());

      this.grid.replaceChildren(frag);

      const selected = this.grid.querySelector('[aria-selected="true"]:not(:disabled)');
      const focusable = selected || firstEnabled;
      if (focusable) focusable.tabIndex = 0;

      this.syncSlotsMaxHeight();
    }

    // Caps the slot list's scroll height at the calendar's real rendered
    // height (5 vs 6 week rows) minus the continue button, so on desktop the
    // two columns never mismatch — short lists still just hug their content.
    syncSlotsMaxHeight() {
      if (!window.matchMedia('(min-width: 990px)').matches) return;
      const calendar = this.querySelector('.ba-calendar');
      if (!calendar || !this.continueButton) return;
      const available = calendar.offsetHeight - this.continueButton.offsetHeight - 12;
      if (available > 0) this.style.setProperty('--ba-slots-max-height', available + 'px');
    }

    newRow(frag) {
      const row = document.createElement('div');
      row.className = 'ba-calendar__row';
      row.setAttribute('role', 'row');
      frag.appendChild(row);
      return row;
    }

    emptyCell() {
      const cell = document.createElement('span');
      cell.className = 'ba-calendar__cell';
      cell.setAttribute('role', 'gridcell');
      cell.setAttribute('aria-hidden', 'true');
      return cell;
    }

    onGridKeydown(event) {
      const moves = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
      if (!(event.key in moves)) return;
      const cells = Array.from(this.grid.querySelectorAll('button.ba-calendar__cell:not(:disabled)'));
      const current = document.activeElement;
      const index = cells.indexOf(current);
      if (index === -1) return;
      event.preventDefault();
      const all = Array.from(this.grid.querySelectorAll('button.ba-calendar__cell'));
      const currentAll = all.indexOf(current);
      let target = null;
      for (let i = currentAll + moves[event.key]; i >= 0 && i < all.length; i += moves[event.key]) {
        if (!all[i].disabled) {
          target = all[i];
          break;
        }
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') break;
      }
      if (target) {
        current.tabIndex = -1;
        target.tabIndex = 0;
        target.focus();
      }
    }

    selectDate(dateKey) {
      this.state.selectedDate = dateKey;
      this.state.selectedSlot = null;
      this.grid.querySelectorAll('button.ba-calendar__cell').forEach((cell) => {
        cell.setAttribute('aria-selected', cell.dataset.date === dateKey ? 'true' : 'false');
      });
      if (this.state.step === 1) this.state.step = 2;
      this.renderSide();
    }

    /* ---------- Availability (via the Calendly proxy worker) ---------- */

    async loadMonth(monthKey) {
      if (!this.config.proxyUrl) {
        this.showError(this.strings.error);
        return;
      }
      const cached = this.state.availability.get(monthKey);
      if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
        this.renderCalendar();
        return;
      }

      if (this.state.fetchController) this.state.fetchController.abort();
      const controller = new AbortController();
      this.state.fetchController = controller;
      this.grid.setAttribute('aria-busy', 'true');
      this.hideError();

      try {
        const response = await fetch(
          this.config.proxyUrl.replace(/\/$/, '') + '/availability?month=' + encodeURIComponent(monthKey),
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error('availability_failed');
        const data = await response.json();

        const slotsByDate = {};
        (data.slots || []).forEach((slot) => {
          const key = this.dateKeyInTz(new Date(slot.start));
          if (!slotsByDate[key]) slotsByDate[key] = [];
          slotsByDate[key].push(slot);
        });
        Object.values(slotsByDate).forEach((slots) =>
          slots.sort((a, b) => new Date(a.start) - new Date(b.start))
        );

        this.state.availability.set(monthKey, {
          fetchedAt: Date.now(),
          duration: data.duration_minutes || 45,
          slotsByDate,
        });

        if (this.state.month === monthKey) {
          this.renderCalendar();
          this.renderSide();
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (this.state.month === monthKey) this.showError(this.strings.error);
      }
    }

    /* ---------- Right column (hint / slots) ---------- */

    renderSide() {
      const { selectedDate, month } = this.state;
      const entry = this.state.availability.get(month);
      const showSlots = !!selectedDate && !!entry;

      this.hint.hidden = showSlots;
      this.slotsEl.hidden = !showSlots;
      this.continueButton.hidden = !showSlots;
      // Recompute now that the button's visibility (and thus its real
      // offsetHeight) just changed — renderCalendar() alone can't see this,
      // since it runs before a date is picked, while the button is still hidden.
      this.syncSlotsMaxHeight();

      if (!showSlots) {
        this.continueButton.disabled = true;
        return;
      }

      const slots = entry.slotsByDate[selectedDate] || [];
      this.slotsEl.replaceChildren();

      if (!slots.length) {
        const empty = document.createElement('p');
        empty.className = 'ba-hint';
        empty.textContent = this.strings.noSlots || '';
        this.slotsEl.appendChild(empty);
        this.continueButton.disabled = true;
        this.updateSlotsScrollState();
        return;
      }

      slots.forEach((slot) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ba-slot';
        button.textContent = this.slotButtonLabel(slot.start, entry.duration);
        const isSelected = this.state.selectedSlot && this.state.selectedSlot.start === slot.start;
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        button.addEventListener('click', () => {
          this.state.selectedSlot = { start: slot.start, duration: entry.duration };
          this.slotsEl
            .querySelectorAll('.ba-slot')
            .forEach((el) => el.setAttribute('aria-pressed', el === button ? 'true' : 'false'));
          this.continueButton.disabled = false;
        });
        this.slotsEl.appendChild(button);
      });

      this.continueButton.disabled = !this.state.selectedSlot;
      this.updateSlotsScrollState();
    }

    // Fades only the edge that actually hides content, so the list never
    // dims a slot the visitor can already see in full.
    updateSlotsScrollState() {
      const el = this.slotsEl;
      const canScrollUp = el.scrollTop > 4;
      const canScrollDown = el.scrollTop < el.scrollHeight - el.clientHeight - 4;
      el.classList.toggle('is-scrollable-up', canScrollUp);
      el.classList.toggle('is-scrollable-down', canScrollDown);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorMessage.textContent = message || 'Something went wrong. Please try again.';
      this.errorEl.hidden = false;
      this.hint.hidden = true;
      this.slotsEl.hidden = true;
      this.continueButton.hidden = true;
      this.grid.setAttribute('aria-busy', 'false');
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    /* ---------- Form ---------- */

    validateForm() {
      let valid = true;
      const setFieldError = (input, errorId, message) => {
        const errorEl = this.querySelector('#' + errorId);
        if (message) {
          valid = false;
          input.setAttribute('aria-invalid', 'true');
          if (errorEl) {
            errorEl.textContent = message;
            errorEl.hidden = false;
          }
        } else {
          input.removeAttribute('aria-invalid');
          if (errorEl) errorEl.hidden = true;
        }
      };

      const name = this.form.elements.name;
      setFieldError(name, name.getAttribute('aria-describedby'), name.value.trim() ? '' : this.strings.nameRequired);

      const email = this.form.elements.email;
      const emailOk = email.value.trim() && email.checkValidity();
      setFieldError(email, email.getAttribute('aria-describedby'), emailOk ? '' : this.strings.emailRequired);

      const terms = this.form.elements.terms;
      const termsError = this.querySelector('#' + terms.getAttribute('aria-describedby'));
      if (!terms.checked) {
        valid = false;
        if (termsError) {
          termsError.textContent = this.strings.termsRequired;
          termsError.hidden = false;
        }
      } else if (termsError) {
        termsError.hidden = true;
      }

      return valid;
    }

    async submit() {
      this.formError.hidden = true;
      if (!this.state.selectedSlot) {
        this.goToStep(1);
        return;
      }
      if (!this.validateForm()) {
        const firstInvalid = this.form.querySelector('[aria-invalid="true"], input[name="terms"]:not(:checked)');
        if (firstInvalid) firstInvalid.focus();
        return;
      }

      const data = new FormData(this.form);
      const payload = {
        start_time: this.state.selectedSlot.start,
        name: (data.get('name') || '').trim(),
        email: (data.get('email') || '').trim(),
        phone: (data.get('phone') || '').trim(),
        interest: data.get('interest') || '',
        guests: Number(data.get('guests') || 0),
        note: (data.get('note') || '').trim(),
        reminder: data.get('reminder') || 'phone',
        marketing_consent: data.get('marketing_consent') === '1',
      };

      const originalLabel = this.submitButton.textContent;
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.submitButton.textContent = this.strings.booking || originalLabel;

      try {
        const response = await fetch(this.config.proxyUrl.replace(/\/$/, '') + '/book', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (response.status === 201) {
          this.confirmSlotEl.textContent = this.selectedSlotHeadline();
          this.goToStep(4);
          return;
        }

        if (response.status === 409) {
          this.state.availability.delete(this.state.month);
          this.state.selectedSlot = null;
          this.goToStep(2);
          this.loadMonth(this.state.month);
          if (this.live) this.live.textContent = this.strings.slotTaken || '';
          this.hint.hidden = true;
          return;
        }

        let message = this.strings.error;
        try {
          const body = await response.json();
          if (body && body.message) message = body.message;
        } catch (parseError) {
          /* keep generic message */
        }
        this.showFormError(message);
      } catch (error) {
        this.showFormError(this.strings.error);
      } finally {
        this.submitButton.disabled = false;
        this.submitButton.removeAttribute('aria-busy');
        this.submitButton.textContent = originalLabel;
      }
    }

    showFormError(message) {
      this.formErrorMessage.textContent = message || 'Something went wrong. Please try again.';
      this.formError.hidden = false;
    }
  }

  if (!customElements.get('book-appointment')) {
    customElements.define('book-appointment', BookAppointment);
  }
})();
